import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import { Button, Spinner } from "reactstrap";
import { useParams } from "react-router-dom";
import { fetchSingleAccount, fetchSingleOpportunity } from "../../opportunities/data/opportunitiesService";
import { useLockState } from "../../../components/userLocks/LockCheck";
import debounce from "lodash/debounce";
import TemplateInputs from "./templateInputs";
import { fetchExportCustomFields } from "../../adminExports/data/customFieldsService";
import { flattenTemplateFields, resolveEffectiveTemplateState } from "../../adminExports/data/customFieldsTree";
import { getRegionDefaultValue } from "../../../utils/regionDefaultValue";
import { plainTextToParagraphHtml } from "../../../utils/richTextUtils";

const TYPE_DATE = "Date";
const normalizeDateValue = (value) => {
    const raw = String(value ?? "").trim();
    if (!raw) return "";

    const ymdPrefixMatch = raw.match(/^(\d{4}-\d{2}-\d{2})/);
    if (ymdPrefixMatch) return ymdPrefixMatch[1];

    const parsed = Date.parse(raw);
    if (Number.isNaN(parsed)) return "";

    return new Date(parsed).toISOString().slice(0, 10);
};

const QuoteContent = ({
    quoteRevision,
    templatesData,
    selectedTemplate,
    setSelectedTemplate,
    onUpdateQuoteTemplateId,
    onUpdateQuoteAdditionalFields,
    customContent,
    activeSectionName,
    hideSectionTabs = false,
}) => {
    const queryClient = useQueryClient();
    const { id } = useParams();
    const lockState = useLockState();

    const { data: opportunityFull } = useQuery({
        queryKey: ["opportunitySingle", id],
        queryFn: () => fetchSingleOpportunity(id),
        staleTime: 60000,
    });

    const { data: accountFull } = useQuery({
        queryKey: ["accountSingle", id],
        queryFn: () => fetchSingleAccount(id),
        staleTime: 60000,
    });

    const [sectionValues, setSectionValues] = useState({});
    const [saveStatus, setSaveStatus] = useState(null); // "saving" | "saved" | "error" | null

    const suppressHydrateRef = useRef(false);
    const hasLocalEditsRef = useRef(false);
    const locallyEditedFieldsRef = useRef(new Set());

    // ---- Race/loop guards ----
    const lastSentRef = useRef(""); // dedupe: skip resending identical values
    const pendingSaveRef = useRef(null); // queue latest while saving

    // ---- Hydration guard (prevents saving blanks on refresh) ----
    const hydratedTemplateIdRef = useRef(null);

    // ------------------- Helpers -------------------
    const coerceToString = (v) => (v === null || v === undefined ? "" : String(v));
    const toBool = (v) => v === true || v === "true" || v === 1 || v === "1";
    const mergeQuoteAdditionalFields = (existingFields, updatedFields) => {
        const getKey = (item) => {
            const field = String(item?.field ?? "").trim();
            if (field) return `field:${field}`;

            const id = String(item?.id ?? "").trim();
            if (id) return `id:${id}`;

            return null;
        };

        const merged = new Map();

        (Array.isArray(existingFields) ? existingFields : []).forEach((item) => {
            const key = getKey(item);
            if (!key || merged.has(key)) return;
            merged.set(key, item);
        });

        // Updated values replace matching existing keys while keeping unrelated fields.
        (Array.isArray(updatedFields) ? updatedFields : []).forEach((item) => {
            const key = getKey(item);
            if (!key) return;
            merged.set(key, item);
        });

        return Array.from(merged.values());
    };

    const {
        data: globalFields = [],
        isLoading: isLoadingGlobalFields,
    } = useQuery({
        queryKey: ["exportCustomFields"],
        queryFn: fetchExportCustomFields,
        staleTime: 60_000,
    });

    const globalFieldsById = useMemo(() => {
        const map = new Map();
        (Array.isArray(globalFields) ? globalFields : []).forEach((field) => {
            if (field?.id === undefined || field?.id === null) return;
            map.set(String(field.id), field);
        });
        return map;
    }, [globalFields]);
    const sectionNameByGuid = useMemo(() => {
        const map = new Map();
        resolveEffectiveTemplateState(selectedTemplate?.customFields, selectedTemplate?.customSections, selectedTemplate?.customFieldsLayout).sections.forEach((section) => {
            if (!section?.guid) return;
            map.set(section.guid, String(section?.name || "").trim());
        });
        return map;
    }, [selectedTemplate]);

    const resolveTemplateSections = useCallback((tpl) => {
        const links = flattenTemplateFields(tpl?.customFields, tpl?.customSections, tpl?.customFieldsLayout);
        return links
            .map((link) => {
                const globalField = globalFieldsById.get(String(link?.id));
                if (!globalField) return null;
                const sectionName = sectionNameByGuid.get(String(link?.sectionGuid || "")) || "";

                return {
                    ...globalField,
                    id: globalField.id,
                    sectionGuid: String(link?.sectionGuid || ""),
                    section: sectionName,
                    sortOrder: link?.sortOrder ?? 0,
                    type: globalField?.type === "TextArea(WYSIWYG)" ? "TextArea" : globalField?.type,
                };
            })
            .filter(Boolean);
    }, [globalFieldsById, sectionNameByGuid]);

    const customFields = useMemo(() => resolveTemplateSections(selectedTemplate), [selectedTemplate, resolveTemplateSections]);
    // Used to determine whether sectionValues is "complete enough" to autosave
    const expectedFieldKeys = useMemo(() => customFields.map((s) => s.field), [customFields]);


    const hydrateSectionValuesFrom = (tpl, revision) => {
        const sections = resolveTemplateSections(tpl);
        const initial = {};

        sections.forEach((s) => {
            let existing = Array.isArray(revision?.quoteAdditionalFields)
                ? revision.quoteAdditionalFields.find((x) => x.field === s.field)?.value
                : undefined;

            if (existing === "" || existing === undefined)
                existing = null;

            if (s.type === "Region") {
                initial[s.field] = existing == null ? getRegionDefaultValue(s.defaultValue) : toBool(existing);
                return;
            }

            if (s.type === TYPE_DATE) {
                const defaultDate = s.salesforceDefaultValue
                    ? opportunityFull?.[s.salesforceDefaultValue.replace("opportunity_", "")] ??
                      accountFull?.[s.salesforceDefaultValue.replace("account_", "")] ??
                      ""
                    : s.defaultValue;

                initial[s.field] = normalizeDateValue(existing ?? defaultDate ?? "");
                return;
            }

            let defaultValue = null;
            if (s.salesforceDefaultValue) {
                defaultValue =
                    opportunityFull?.[s.salesforceDefaultValue.replace("opportunity_", "")] ??
                    accountFull?.[s.salesforceDefaultValue.replace("account_", "")] ??
                    "";
                if (s.type === "TextArea") defaultValue = plainTextToParagraphHtml(defaultValue);
            } else {
                defaultValue = s.defaultValue;
            }

            initial[s.field] = coerceToString(existing ?? defaultValue ?? "");
        });

        return initial;
    };

    const markHydrated = useCallback((tplId, values) => {
        hydratedTemplateIdRef.current = tplId ?? null;
        hasLocalEditsRef.current = false;

        // IMPORTANT: prevent autosave from firing for this freshly-hydrated state
        lastSentRef.current = JSON.stringify(values ?? {});
        pendingSaveRef.current = null;
    }, []);

    const isHydratedForCurrentTemplate = () => {
        const tplId = selectedTemplate?.id ?? null;
        return tplId && hydratedTemplateIdRef.current === tplId;
    };

    const isSectionValuesCompleteEnough = () => {
        if (!expectedFieldKeys.length) return false;
        for (const k of expectedFieldKeys) {
            if (!(k in (sectionValues ?? {}))) return false;
        }
        return true;
    };

    // ------------------- Initial selection from server -------------------
    useEffect(() => {
        if (!opportunityFull || !accountFull) return;
        if (suppressHydrateRef.current) return;
        if (!templatesData?.length || !quoteRevision) return;

        const templateId = quoteRevision?.quoteTemplateId ?? quoteRevision?.selectedTemplateId ?? null;
        if (!templateId) return;
        if (selectedTemplate?.id === templateId) return;

        const match = templatesData.find((t) => t.id === templateId);
        if (match) {
            setSelectedTemplate(match);

            // hydrate immediately and mark hydrated so autosave won't run with blanks
            const initial = hydrateSectionValuesFrom(match, quoteRevision);
            setSectionValues((prev) => (Object.keys(prev).length === 0 ? initial : prev));
            markHydrated(match.id, initial);
        }
    }, [
        templatesData,
        quoteRevision,
        selectedTemplate,
        opportunityFull,
        accountFull,
        setSelectedTemplate,
        markHydrated,
        customContent
    ]);

    useEffect(() => {
        // Sync external quote field updates into local form state without replacing the whole object.
        // A full replace can drop expected keys and block autosave completeness checks.
        const fieldsByName = new Map((customFields || []).map((field) => [field.field, field]));
        if (!fieldsByName.size) return;

        const incoming = Object.fromEntries(
            (Array.isArray(customContent) ? customContent : [])
                .filter((item) => fieldsByName.has(item?.field))
                .map((item) => [
                    item.field,
                    fieldsByName.get(item.field).type === "Region" ? toBool(item.value) : item.value,
                ])
        );

        setSectionValues((prev) => {
            // If local state is empty (first load), start from hydrated defaults so all expected
            // fields remain present before merging incoming values.
            const base = prev && Object.keys(prev).length
                ? prev
                : hydrateSectionValuesFrom(selectedTemplate, quoteRevision);

            let changed = false;
            const next = { ...base };

            for (const [field, value] of Object.entries(incoming)) {
                if (next[field] !== value) {
                    next[field] = value;
                    changed = true;
                }
            }

            // Return previous reference when nothing changed to avoid unnecessary re-renders.
            return changed ? next : base;
        });
    }, [customContent, customFields, selectedTemplate, quoteRevision, opportunityFull, accountFull])


    // ------------------- Mutations -------------------
    const saveSectionsMutation = useMutation({
        mutationFn: async ({ tpl, values }) => {
            const payload = structuredClone(quoteRevision ?? {});
            payload.quoteTemplateId = tpl?.id ?? payload.quoteTemplateId ?? null;

            const updatedFields = customFields.map((s) => ({
                id: s.id,
                field: s.field,
                value:
                    s.type === "Region"
                        ? String(!!values?.[s.field])
                        : s.type === TYPE_DATE
                        ? normalizeDateValue(values?.[s.field])
                        : coerceToString(values?.[s.field] ?? ""),
            }));

            payload.quoteAdditionalFields = mergeQuoteAdditionalFields(
                quoteRevision?.quoteAdditionalFields,
                updatedFields
            );

            return payload;
        },
        onMutate: () => setSaveStatus("saving"),
        onSuccess: (payload, variables) => {
            onUpdateQuoteTemplateId(variables?.tpl?.id ?? null);
            onUpdateQuoteAdditionalFields(payload.quoteAdditionalFields);

            if (pendingSaveRef.current) {
                setSaveStatus("saving");
            } else {
                setTimeout(() => setSaveStatus("saved"), 1000);
                setTimeout(() => setSaveStatus(null), 1200);
            }


        },
        onError: () => setSaveStatus("error"),
    });

    // ------------------- Handlers -------------------
    const onInputChanged = (fieldName, fieldValue) => {
        hasLocalEditsRef.current = true;
        locallyEditedFieldsRef.current.add(fieldName);
        setSaveStatus("saving");

        const next = {
            ...sectionValues,
            [fieldName]: fieldValue,
        };

        const updatedFields = customFields.map((s) => ({
            id: s.id,
            field: s.field,
            value:
            s.type === "Region"
                ? String(!!next?.[s.field])
                : s.type === TYPE_DATE
                ? normalizeDateValue(next?.[s.field])
                : coerceToString(next?.[s.field] ?? ""),
        }));

        const mergedFields = mergeQuoteAdditionalFields(
            quoteRevision?.quoteAdditionalFields,
            updatedFields
        );

        setSectionValues(next);
        onUpdateQuoteAdditionalFields(mergedFields);
    };


    // ---- Save orchestration: dedupe + no overlap + queue latest ----
    const requestSave = useCallback(
        (tpl, values) => {
            if (!tpl || !quoteRevision?.id) return;

            if (saveSectionsMutation.isPending) {
                pendingSaveRef.current = { tpl, values };
                return;
            }

            saveSectionsMutation.mutate({ tpl, values });
        },
        [quoteRevision?.id, saveSectionsMutation]
    );

    useEffect(() => {
        if (saveSectionsMutation.isPending) return;

        if (pendingSaveRef.current) {
            const next = pendingSaveRef.current;
            pendingSaveRef.current = null;

            lastSentRef.current = JSON.stringify(next.values ?? {});
            requestSave(next.tpl, next.values);
        }
    }, [saveSectionsMutation.isPending, requestSave]);

    const maybeSave = useCallback(
        (tpl, values) => {
            if (!tpl) return;

            const snapshot = JSON.stringify(values ?? {});
            if (snapshot === lastSentRef.current) {
                if (!saveSectionsMutation.isPending && !pendingSaveRef.current) {
                    setSaveStatus(null);
                }
                return;
            }

            lastSentRef.current = snapshot;
            requestSave(tpl, values);
        },
        [requestSave, saveSectionsMutation.isPending]
    );

    // Stable debounce created once, calls latest maybeSave
    const maybeSaveRef = useRef(maybeSave);
    useEffect(() => {
        maybeSaveRef.current = maybeSave;
    }, [maybeSave]);

    const debouncedSave = useMemo(
        () =>
            debounce((tpl, values) => {
                maybeSaveRef.current(tpl, values);
            }, 500),
        []
    );

    useEffect(() => () => debouncedSave.cancel(), [debouncedSave]);

    // ✅ Autosave effect with hydration + completeness guards
    useEffect(() => {
        if (!selectedTemplate) return;

        // Do not autosave until we have hydrated for this template (prevents refresh-blank overwrite)
        if (!isHydratedForCurrentTemplate()) return;

        // Do not autosave until values are populated for all expected fields
        if (!isSectionValuesCompleteEnough()) return;

        console.log("DEBOUNCE CALLED");
        debouncedSave(selectedTemplate, sectionValues);
    }, [sectionValues, selectedTemplate, debouncedSave, expectedFieldKeys]); // expectedFieldKeys affects completeness

    // Complete hydration without overwriting any fields the user edited while data was loading.
    useEffect(() => {
        if (!selectedTemplate || !quoteRevision || !opportunityFull || !accountFull) return;
        if (hydratedTemplateIdRef.current === selectedTemplate.id) return;

        const initial = hydrateSectionValuesFrom(selectedTemplate, quoteRevision);
        const resolvedValues = { ...initial };
        const fieldsByName = new Map((customFields || []).map((field) => [field.field, field]));
        (Array.isArray(customContent) ? customContent : []).forEach((item) => {
            const field = fieldsByName.get(item?.field);
            if (!field) return;

            resolvedValues[item.field] = field.type === "Region" ? toBool(item.value) : item.value;
        });

        const hydratedValues = { ...resolvedValues };
        locallyEditedFieldsRef.current.forEach((fieldName) => {
            if (fieldName in sectionValues) {
                hydratedValues[fieldName] = sectionValues[fieldName];
            }
        });

        setSectionValues(hydratedValues);
        locallyEditedFieldsRef.current.clear();

        // Use resolved values as the baseline so calculated fields do not trigger an autosave.
        markHydrated(selectedTemplate.id, resolvedValues);
    }, [selectedTemplate, quoteRevision, opportunityFull, accountFull, sectionValues, customContent, customFields, markHydrated]);

    // ------------------- Render -------------------
    return (
        <div className={`${lockState?.isLocked ? "locked" : ""} d-flex flex-column h-100`} style={{ minHeight: 0 }}>
            {selectedTemplate && (
                <>
                    {customFields.length > 0 ? (
                        <div
                            className="flex-grow-1"
                            style={{ minHeight: 0, overflow: "hidden" }}
                        >
                            <TemplateInputs
                                selectedTemplate={selectedTemplate}
                                sectionValues={sectionValues}
                                onInputChanged={onInputChanged}
                                saveStatus={saveStatus}
                                activeSectionOverride={activeSectionName}
                                hideSectionTabs={hideSectionTabs}
                            />
                        </div>
                    ) : isLoadingGlobalFields ? (
                        <div className="d-flex align-items-center gap-2 text-muted mt-3">
                            <Spinner size="sm" />
                            <span>Loading template inputs...</span>
                        </div>
                    ) : (
                        <div className="text-muted mt-3">No custom sections for this template.</div>
                    )}
                </>
            )}
        </div>
    );
};

export default QuoteContent;
